import Component from "@glimmer/component";
import { guidFor } from "@ember/object/internals";
import { cached, tracked } from "@glimmer/tracking";
import { modifier } from "ember-modifier";
import mergeClasses from "glesys-controlpanel/modifiers/merge-classes";
import ENV from "glesys-controlpanel/config/environment";
import IconsChevronDown from "glesys-controlpanel/components/icons/chevron-down";

export interface DropdownSignature {
  Args: {
    chevron?: boolean;
  };
  Blocks: {
    header: [];
    default: [];
  };
  Element: HTMLButtonElement;
}

const FOCUSABLE_ELEMENTS = [
  "a:not([hidden]):not([aria-disabled='true'])",
  "button:not([disabled]):not([hidden])",
  "input:not([disabled]):not([hidden])",
  "[tabindex]:not([hidden]):not([tabindex^='-'])",
];

export default class Dropdown extends Component<DropdownSignature> {
  @tracked currentIndex = -1;
  searchKeys = "";
  searchTimeout: number | undefined;
  @tracked isOpen = false;

  list!: HTMLDivElement;

  get chevron() {
    return this.args.chevron ?? true;
  }

  get items(): HTMLElement[] {
    return [...this.list.querySelectorAll(FOCUSABLE_ELEMENTS.join(","))].filter((item) => {
      const style = window.getComputedStyle(item);
      return style.display !== "none" && style.visibility !== "hidden" && style.opacity !== "0";
    }) as HTMLElement[];
  }

  @cached
  get guid(): string {
    return guidFor(this);
  }

  selectItem(index: number) {
    this.items?.at(index)?.focus();
  }

  closeDropdownOnClick = modifier((element: HTMLDivElement) => {
    const closePopover = () => element.hidePopover();

    element.addEventListener("click", closePopover);

    return () => element.removeEventListener("click", closePopover);
  });

  setupListeners = modifier((element: HTMLButtonElement) => {
    this.list = document.querySelector(`#${this.guid}`) as HTMLDivElement;

    element.addEventListener("keydown", this.onButtonKeyDown);
    this.list.addEventListener("toggle", this.onToggleList);

    return () => {
      this.list?.removeEventListener("toggle", this.onToggleList);
      element.removeEventListener("keydown", this.onButtonKeyDown);
      document.removeEventListener("keydown", this.onKeyDown, { capture: true });
    };
  });

  onButtonKeyDown = (event: KeyboardEvent) => {
    switch (event.key) {
      case "ArrowDown":
        // @ts-expect-error: passing options to showPopover is quite new and not in TypeScript's DOM typings yet
        this.list?.showPopover({ source: event.target });
        this.currentIndex = 0;
        this.selectItem(this.currentIndex);
        break;
      case "ArrowUp":
        // @ts-expect-error: passing options to showPopover is quite new and not in TypeScript's DOM typings yet
        this.list?.showPopover({ source: event.target });
        this.currentIndex = this.items.length - 1;
        this.selectItem(this.currentIndex);
        break;
    }
  };

  onToggleList = (event: ToggleEvent) => {
    if (event.newState === "open") {
      this.isOpen = true;
      document.addEventListener("keydown", this.onKeyDown, { capture: true });
    } else {
      this.isOpen = false;
      document.removeEventListener("keydown", this.onKeyDown, { capture: true });
      this.currentIndex = -1;
      this.searchKeys = "";
      clearTimeout(this.searchTimeout);
    }
  };

  onKeyDown = (event: KeyboardEvent) => {
    switch (event.key) {
      case "Home": {
        event.preventDefault();
        this.currentIndex = 0;
        this.selectItem(this.currentIndex);
        break;
      }
      case "End": {
        event.preventDefault();
        this.currentIndex = this.items.length - 1;
        this.selectItem(this.currentIndex);
        break;
      }
      case "ArrowDown": {
        event.preventDefault();
        this.currentIndex = (this.currentIndex + 1) % this.items.length;
        this.selectItem(this.currentIndex);
        break;
      }
      case "ArrowUp": {
        event.preventDefault();
        this.currentIndex =
          this.currentIndex < 0
            ? this.items.length - 1
            : (this.currentIndex - 1 + this.items.length) % this.items.length;

        this.selectItem(this.currentIndex);
        break;
      }
      case "Enter": {
        setTimeout(() => this.list?.hidePopover(), 0);
        break;
      }
      default:
        this.handleTypeToSearch(event);
    }
  };

  isNonCharacterKey(key?: string): boolean {
    // all non character keys should have a key length greater than 1, e.g. "Enter", "ArrowDown", "Shift" etc.
    return key?.length !== 1;
  }

  handleTypeToSearch(event: KeyboardEvent) {
    if (this.isNonCharacterKey(event.key)) {
      return;
    }

    this.searchKeys += event.key.toLowerCase();

    const matchingItem = this.items.findIndex((item) =>
      item.textContent?.trim().toLowerCase().startsWith(this.searchKeys),
    );

    if (matchingItem !== -1) {
      this.currentIndex = matchingItem;
      this.selectItem(this.currentIndex);
    }

    clearTimeout(this.searchTimeout);

    //  istanbul can't ignore if-else in a ternary so we ignore the whole line
    // istanbul ignore next
    const timeoutDuration = ENV.environment === "test" ? 10 : 1000;
    this.searchTimeout = setTimeout(() => (this.searchKeys = ""), timeoutDuration);
  }

  <template>
    <div class="[anchor-scope:all]">
      <button
        class="[anchor-name:--dropdown-button] flex items-center gap-2 text-gray-500 hover:text-gray-800 focus:text-gray-800 cursor-pointer disabled:cursor-not-allowed"
        aria-haspopup="true"
        aria-controls={{this.guid}}
        data-test-dropdown
        type="button"
        popovertarget={{this.guid}}
        {{this.setupListeners}}
        {{mergeClasses}}
        ...attributes
      >
        <span class="flex items-center" data-test-dropdown-header>{{yield to="header"}}</span>
        {{#if this.chevron}}
          <IconsChevronDown
            class="{{if this.isOpen 'rotate-180'}} transition-transform origin-center"
            data-test-dropdown-chevron
          />
        {{/if}}
      </button>

      <div
        class="absolute border border-gray-200 rounded-sm inset-auto [position-anchor:--dropdown-button] top-[calc(anchor(bottom)+--spacing(3))] right-[anchor(right)] [position-try-fallbacks:flip-inline_flip-block] transition transition-discrete origin-top duration-300 ease-in-out opacity-0 starting:open:opacity-0 open:opacity-100"
        id={{this.guid}}
        role="menu"
        data-test-dropdown-content
        popover="auto"
        {{this.closeDropdownOnClick}}
      >
        {{yield}}
      </div>
    </div>
  </template>
}
